import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Copy, Check, ShieldCheck } from 'lucide-react';
import { PLUGIN_PERMISSION_DESCRIPTIONS, isValidPluginPermission } from '@fias/arche-sdk';
import { api, MarketplaceArcheDetail } from '../../lib/api';
import { FavoriteButton } from '../../arche/components/FavoriteButton';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { archePath } from '@/lib/arche-url';
import { shareOrigin } from '@/lib/portal-host';
import { ListingStrip } from '../components/ListingCard';
import { useOldStoreName } from '../old-store-name';

export function ArcheDetailPage() {
  const { id } = useParams<{ id: string }>();
  const oldStore = useOldStoreName();
  const navigate = useNavigate();
  const [arche, setArche] = useState<MarketplaceArcheDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embedCopied, setEmbedCopied] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchArche = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await api.getMarketplaceArche(id);
        setArche(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load arche');
        setArche(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArche();
  }, [id]);

  const handleRun = () => {
    if (arche) {
      navigate(`/a/${archePath(arche)}`);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-64 bg-muted rounded-lg mb-6" />
            <div className="h-8 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-full mb-2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !arche) {
    return (
      <div className="py-16">
        <div className="max-w-lg mx-auto text-center px-4">
          <h1 className="text-2xl font-bold text-foreground mb-4">Arche Not Found</h1>
          <p className="text-muted-foreground mb-8">
            {error || "The arche you're looking for doesn't exist or has been removed."}
          </p>
          <Link
            to={oldStore.homePath}
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
          >
            Back to {oldStore.name}
          </Link>
        </div>
      </div>
    );
  }

  const hasBaseCost = arche.pricing.priceCents != null && arche.pricing.priceCents > 0;
  const hasActionPricing =
    arche.actionPricing != null && Object.keys(arche.actionPricing).length > 0;
  // Any model other than the explicitly-free ones ('free' / 'included') bills the user for
  // usage as the arche runs, so it must not read "Free". ('fixed' is excluded — it shows
  // its base price via hasBaseCost.)
  const billsUsage =
    arche.pricing.model === 'no_base_cost' ||
    arche.pricing.model === 'per_use' ||
    arche.pricing.model === 'tiered';
  const hasUsageFees = arche.hasPerUseCredits || hasActionPricing || billsUsage;

  const pricingLabel = hasBaseCost
    ? hasUsageFees
      ? `$${(arche.pricing.priceCents! / 100).toFixed(2)} plus additional usage fees`
      : `$${(arche.pricing.priceCents! / 100).toFixed(2)}`
    : hasUsageFees
      ? 'No Base Cost'
      : 'Free';

  return (
    <div>
      {/* Breadcrumb */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to={oldStore.homePath} className="hover:text-foreground">
              {oldStore.name}
            </Link>
            <span>/</span>
            {arche.categorySlug && arche.categoryName && (
              <>
                <Link
                  to={`/marketplace/categories/${arche.categorySlug}`}
                  className="hover:text-foreground"
                >
                  {arche.categoryName}
                </Link>
                <span>/</span>
              </>
            )}
            <span className="text-foreground">{arche.name}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex justify-center mb-6">
              {arche.imageRef ? (
                <ArcheIconImg
                  imageRef={arche.imageRef}
                  size={512}
                  alt={arche.name}
                  className="w-full max-w-[512px] aspect-square rounded-2xl object-cover shadow-sm"
                />
              ) : (
                <DefaultArcheIcon className="w-full max-w-[512px] aspect-square rounded-2xl shadow-sm" />
              )}
            </div>

            {/* The byline carries the gap when there is one, so the spacing below
                the title is unchanged for an arche with no resolvable publisher. */}
            <h1
              className={`text-3xl font-bold text-foreground ${arche.publisherName ? 'mb-2' : 'mb-4'}`}
            >
              {arche.name}
            </h1>

            {/* Publisher byline. Platform-built arches resolve to the Fias legal
                name server-side; third-party plugins show their contributor. */}
            {arche.publisherName && (
              <p className="text-sm text-muted-foreground mb-4">
                By <span className="font-medium text-foreground">{arche.publisherName}</span>
              </p>
            )}

            <p className="text-lg text-muted-foreground mb-6">{arche.simpleDescription}</p>

            {arche.expandedDescription && (
              <div className="prose max-w-none mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-3">About</h2>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {arche.expandedDescription}
                </p>
              </div>
            )}

            {/* Its extra Store listings: the games (or other entry points)
                inside it, each opening at its own path. */}
            {(arche.listings?.length ?? 0) > 0 && (
              <ListingStrip listings={arche.listings!} title={`Also in ${arche.name}`} />
            )}

            {/* Permissions — the first user-facing disclosure of what a
                plugin can do. Rendered for every plugin arche that declares
                any; user-data access is called out distinctly. */}
            {(arche.pluginConfig?.permissions?.length ?? 0) > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-foreground mb-3">Permissions</h2>
                <ul className="space-y-2">
                  {(arche.pluginConfig?.permissions ?? [])
                    .filter(isValidPluginPermission)
                    .map((permission) => (
                      <li key={permission} className="flex items-start gap-2 text-sm">
                        <ShieldCheck
                          className={`h-4 w-4 mt-0.5 shrink-0 ${
                            // Both halves of the user-documents pair reach the
                            // user's OWN files — reading what they have, or
                            // putting things into it. This is the first place
                            // anyone sees what an app can do, so neither should
                            // read as unremarkable next to `theme:read`.
                            permission === 'vault:user-documents:read' ||
                            permission === 'vault:user-documents:write' ||
                            permission === 'vault:user-documents:edit' ||
                            permission === 'vault:arche-saves:write' ||
                            permission === 'vault:workspace-documents:read'
                              ? 'text-amber-500'
                              : 'text-muted-foreground'
                          }`}
                        />
                        <span className="text-muted-foreground">
                          {PLUGIN_PERMISSION_DESCRIPTIONS[permission]}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {arche.tags.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-foreground mb-3">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {arche.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 sticky top-8">
              <button
                onClick={handleRun}
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors font-medium mb-4"
              >
                {arche.isStatic ? 'Open Arche' : 'Run Arche'}
              </button>
              <div className="flex items-center justify-center mb-6">
                <FavoriteButton archeId={arche.archId} showLabel className="px-3 py-1.5" />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Pricing</span>
                  <span className="font-medium text-foreground">{pricingLabel}</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Quality Score</span>
                  <span className="font-medium text-foreground">
                    {(arche.fitnessScore * 100).toFixed(0)}%
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Success Rate</span>
                  <span className="font-medium text-foreground">
                    {(arche.successRate * 100).toFixed(1)}%
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Times Used</span>
                  <span className="font-medium text-foreground">
                    {arche.invocationCount.toLocaleString()}
                  </span>
                </div>

                {arche.avgCompletionTimeMs != null && arche.avgCompletionTimeMs > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Avg. Duration</span>
                    <span className="font-medium text-foreground">
                      {arche.avgCompletionTimeMs < 1000
                        ? `${arche.avgCompletionTimeMs}ms`
                        : `${(arche.avgCompletionTimeMs / 1000).toFixed(1)}s`}
                    </span>
                  </div>
                )}

                {arche.publisherName && (
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Publisher</span>
                    <span className="font-medium text-foreground">{arche.publisherName}</span>
                  </div>
                )}

                {arche.categoryName && (
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Category</span>
                    <Link
                      to={`/marketplace/categories/${arche.categorySlug}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {arche.categoryName}
                    </Link>
                  </div>
                )}

                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium text-foreground">
                    {arche.isStatic ? 'Static' : 'Dynamic'}
                  </span>
                </div>
              </div>

              {/* Embed Code Section - only for public arches */}
              {arche.isPublic && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Embed This Arche</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add this arche to your website with an iframe:
                  </p>
                  <div className="relative">
                    <pre className="bg-muted rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">
                      <code>{`<iframe
  src="${shareOrigin()}/a/${archePath(arche)}?embed=true"
  width="100%"
  height="600"
  frameborder="0"
></iframe>`}</code>
                    </pre>
                    <button
                      onClick={() => {
                        const embedCode = `<iframe
  src="${shareOrigin()}/a/${archePath(arche)}?embed=true"
  width="100%"
  height="600"
  frameborder="0"
></iframe>`;
                        navigator.clipboard.writeText(embedCode);
                        setEmbedCopied(true);
                        setTimeout(() => setEmbedCopied(false), 2000);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded bg-background/80 hover:bg-background border border-border transition-colors"
                      title="Copy embed code"
                    >
                      {embedCopied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
